import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { TextAlign, TextMeasure, TextOverflow, TextTone } from "./Text";

const HEADING_VARIANT_CLASSES = {
  1: "text-heading-1",
  2: "text-heading-2",
  3: "text-heading-3",
  4: "text-heading-4",
} as const;

const HEADING_LIGHT_VARIANT_CLASSES = {
  1: "text-heading-1-light",
  2: "text-heading-2-light",
} as const;

const HEADING_TONE_CLASSES: Record<TextTone, string> = {
  inherit: "",
  default: "text-text-primary",
  secondary: "text-text-secondary",
  tertiary: "text-text-tertiary",
  brand: "text-text-brand",
  "brand-secondary": "text-text-brand-secondary",
  success: "text-text-success",
  warning: "text-text-warning",
  danger: "text-text-danger",
  "danger-secondary": "text-text-danger-secondary",
  inverse: "text-text-default-inverse",
  disabled: "text-text-disabled",
};

const HEADING_MEASURE_CLASSES: Record<TextMeasure, string> = {
  compact: "max-w-measure-compact",
  default: "max-w-measure-default",
  reading: "max-w-measure-reading",
  wide: "max-w-measure-wide",
};

const HEADING_ALIGN_CLASSES: Record<TextAlign, string> = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
};

const HEADING_OVERFLOW_CLASSES: Record<TextOverflow, string> = {
  normal: "",
  wrap: "wrap-break-word",
  preserve: "whitespace-pre-line wrap-break-word",
  nowrap: "whitespace-nowrap",
  truncate: "truncate",
  "line-clamp-one": "line-clamp-1",
  balance: "text-balance",
};

export type HeadingLevel = 1 | 2 | 3 | 4;
type HeadingElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

type HeadingWeightProps = { level: 1 | 2; weight?: "default" | "light" } | { level: 3 | 4; weight?: "default" };

export type HeadingProps<TElement extends HeadingElement = HeadingElement> = {
  children: ReactNode;
  tone?: TextTone;
  measure?: TextMeasure;
  align?: TextAlign;
  overflow?: TextOverflow;
} & HeadingWeightProps &
  DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLHeadingElement>;

export function Heading<TElement extends HeadingElement = HeadingElement>({
  as,
  align,
  children,
  level,
  measure,
  overflow = "balance",
  ref,
  tone = "default",
  weight = "default",
  ...nativeProps
}: HeadingProps<TElement>): ReactNode {
  const Element = as ?? (`h${level}` as HeadingElement);
  const variantClass =
    weight === "light" ? HEADING_LIGHT_VARIANT_CLASSES[level as 1 | 2] : HEADING_VARIANT_CLASSES[level];

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin(
        variantClass,
        HEADING_TONE_CLASSES[tone],
        HEADING_OVERFLOW_CLASSES[overflow],
        align && HEADING_ALIGN_CLASSES[align],
        measure && HEADING_MEASURE_CLASSES[measure],
      )}
    >
      {children}
    </Element>
  );
}
